import Grid from '@mui/material/Grid';
import { CheckboxOne } from '../../../../components/CheckboxOne/CheckboxOne.component';
import { ModalWithButtons } from '../../../../components/ModalWithButtons/ModalWithButtons.component';
import { ExamTaskGroupSolutionScoreFlag, ExaminerFunction, getExaminerFunctionLabel } from '../../Assessment.types';
import CloseIcon from '@material-ui/icons/Close';
import { ButtonOne } from '../../../../components/ButtonOne/ButtonOne.component';
import { useEffect, useState } from 'react';
import { assignFlagToExamTaskGroupSolutionScore } from '../../../../services/scoring/scoring.service';
import { useTranslation } from 'react-i18next';
import { FormHelperTextProps, IconButton, TextField, Typography } from '@mui/material';
import { useLayoutContext } from '../../../../Context/Layout.context';
import { FlagIcon } from '../../../../components/SystemSettings/SystemFlags/Subcomponents/FlagIcon.component';
import { FlagType, FlagsDto } from '../../../../components/SystemSettings/SystemFlags/Subcomponents/Flag.types';
import DisabledIcon from '@mui/icons-material/HideSource';
import TooltipOne from '../../../../components/TooltipOne/TooltipOne.component';
import AddCommentIcon from '@mui/icons-material/MapsUgcOutlined';
import DeleteCommentIcon from '@mui/icons-material/Clear';
import { ValidationRow } from '../../../../components/ValidationRow/ValidationRow.components';
import useSessionUser from '../../../../hooks/useSessionUser';

interface AssignFlagsProps {
  examTaskGroupSolutionScoreId: string;
  assignedFlags: ExamTaskGroupSolutionScoreFlag[];
  allAvailableFlags: FlagType[];
  onSave: () => void;
}

interface CheckableFlag {
  flagId: string;
  flag: FlagType;
  isChecked: boolean;
  examinerFunction?: ExaminerFunction;
  examinerId?: string;
  comment?: string | null;
}

export const AssignFlags: React.FC<AssignFlagsProps> = (props) => {
  const { t } = useTranslation();
  const [checkableFlags, setCheckableFlags] = useState<CheckableFlag[]>([]);
  const [initialCheckableFlags, setInitialCheckableFlags] = useState<CheckableFlag[]>([]);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const [isCommentIconFocused, setIsCommentIconFocused] = useState<boolean>(false);

  const { openAlert, showLoader } = useLayoutContext();
  const { user } = useSessionUser();

  const examinerId = user?.profile.sub;

  const COMMENT_MAX_LENGTH = 256;

  const displayError = () => openAlert('error');

  const isDisabled = (checkableFlag: CheckableFlag) => {
    return props.assignedFlags.some((e) => e.flagDefinitionId === checkableFlag.flag.id && e.examinerId !== examinerId);
  };

  const onClear = () => {
    setCheckableFlags((prevstate) =>
      prevstate.map((item) => {
        if (!isDisabled(item) && item.flagId) {
          return {
            ...item,
            flagId: item.flagId,
            flag: item.flag,
            isChecked: false,
            examinerFunction: item.examinerFunction,
            examinerId: item.examinerId,
            comment: '',
          };
        } else return item;
      }),
    );
  };

  const closeDialog = () => props.onSave();

  const handleAddCommentOpen = (checkableFlag: CheckableFlag) => {
    setCheckableFlags((prevstate) =>
      prevstate.map((item) => {
        if (item.flag.id === checkableFlag.flag.id) {
          return {
            ...item,
            comment: ' ',
          };
        } else return item;
      }),
    );
  };

  const handleChangeComment = (checkableFlag: CheckableFlag, value: string) => {
    setCheckableFlags((prevstate) =>
      prevstate.map((item) => {
        if (item.flag.id === checkableFlag.flag.id) {
          return {
            ...item,
            comment: value,
          };
        } else return item;
      }),
    );
  };

  const handleDeleteComment = (checkableFlag: CheckableFlag) => {
    setCheckableFlags((prevstate) =>
      prevstate.map((item) => {
        if (item.flag.id === checkableFlag.flag.id) {
          return {
            ...item,
            comment: null,
          };
        } else return item;
      }),
    );
  };

  useEffect(() => {
    const flags = props.allAvailableFlags.reduce((result: CheckableFlag[], flag: FlagType) => {
      const isChecked = props.assignedFlags.some((activeFlag: ExamTaskGroupSolutionScoreFlag) => {
        return activeFlag.flagDefinitionId === flag.id;
      });
      if (flag.active || isChecked) {
        const examinerFunction = props.assignedFlags.filter((e) => e.flagDefinitionId === flag.id)[0]?.examinerFunction;
        const comment = props.assignedFlags.filter((e) => e.flagDefinitionId === flag.id)[0]?.comment;
        const flagId = props.assignedFlags.filter((e) => e.flagDefinitionId === flag.id)[0]?.flagId;
        result.push({ flagId, flag, isChecked, examinerFunction, comment });
      }
      return result;
    }, []);
    setCheckableFlags(flags);
    const intitialFlags = JSON.parse(JSON.stringify(flags));
    setInitialCheckableFlags(intitialFlags);
  }, [props.assignedFlags, props.allAvailableFlags]);

  const onCheckboxChange = (checkableFlag: CheckableFlag, index: number) => {
    const checkablesCopy = [...checkableFlags];
    checkablesCopy[index].isChecked = !checkablesCopy[index].isChecked;
    if (!checkablesCopy[index].isChecked) {
      handleDeleteComment(checkableFlag);
    } else {
      setCheckableFlags(checkablesCopy);
    }
  };

  const isCommentToLong = (comment: string) => {
    return comment.length > COMMENT_MAX_LENGTH;
  };

  useEffect(() => {
    if (
      checkableFlags.some((e) => e.comment === ' ') ||
      checkableFlags.some((e) => e.comment && isCommentToLong(e.comment))
    ) {
      setIsDataChanged(false);
    } else setIsDataChanged(JSON.stringify(initialCheckableFlags) !== JSON.stringify(checkableFlags));
  }, [checkableFlags]);

  const handlOnSave = () => {
    const changedFlags: CheckableFlag[] = [];
    checkableFlags.map((item) => {
      if (JSON.stringify(initialCheckableFlags.filter((e) => e.flag.id === item.flag.id)) !== JSON.stringify([item])) {
        changedFlags.push(item);
      }
    });

    const bodyRequest: FlagsDto[] = changedFlags.map(({ flagId, flag, comment, isChecked }) => ({
      flagId: flagId,
      flagDefinitionId: flag.id,
      comment: comment?.trim(),
      isDeleted: isChecked ? false : true,
    }));

    showLoader(true);
    assignFlagToExamTaskGroupSolutionScore(props.examTaskGroupSolutionScoreId, bodyRequest).then(
      () => {
        showLoader(false);
        openAlert('success');
        setTimeout(() => props.onSave(), 2000);
      },
      () => {
        showLoader(false);
        displayError;
      },
    );
  };

  const helperTextStyle = {
    display: 'flex',
    position: 'relative',
    left: '-10px',
    minWidth: '120%',
  };

  return (
    <ModalWithButtons
      open
      size="sm"
      autoHeight
      title={`Wybierz znacznik flagi dla RGZ: ${props.examTaskGroupSolutionScoreId}`}
      xCloseButton={closeDialog}
      onConfirm={handlOnSave}
      confirmButtonDisabled={!isDataChanged}
    >
      <Grid container>
        {checkableFlags.map((checkableFlag, index) => {
          return (
            <Grid
              xs={6}
              key={checkableFlag.flag.id}
              onKeyDown={(event: React.KeyboardEvent) => {
                if (isCommentIconFocused) return;
                if (event.key === 'Enter') {
                  if (!checkableFlag.comment) {
                    event.preventDefault();

                    onCheckboxChange(checkableFlag, index);
                  }
                }
              }}
              tabIndex={0}
            >
              <Grid key={checkableFlag.flag.id} item xs={6} display="flex" alignItems="center" flexDirection="row">
                <TooltipOne
                  title={
                    isDisabled(checkableFlag)
                      ? 'Flaga danego koloru została już dodana do tego RGZ przez innego użytkownika'
                      : ''
                  }
                  placement="top"
                >
                  <Grid flexWrap="nowrap" display="flex" alignItems="center">
                    <CheckboxOne
                      data-testid={`${checkableFlag.flag.name}-checkbox`}
                      checked={checkableFlag.isChecked}
                      inputProps={{ 'aria-label': checkableFlag.flag.name }}
                      onChange={() => onCheckboxChange(checkableFlag, index)}
                      disabled={isDisabled(checkableFlag)}
                    />
                    {isDisabled(checkableFlag) && <DisabledIcon fontSize="small" style={{ color: '#81818D' }} />}
                    <FlagIcon
                      id={checkableFlag.flag.id}
                      name={checkableFlag.flag.name}
                      color={checkableFlag.flag.color}
                    />
                    <Typography noWrap>
                      {checkableFlag.flag.name}
                      {checkableFlag.examinerFunction !== undefined
                        ? ` [${getExaminerFunctionLabel(checkableFlag.examinerFunction)}] `
                        : ''}
                    </Typography>
                  </Grid>
                </TooltipOne>
                {checkableFlag.isChecked && !isDisabled(checkableFlag) && (
                  <Grid>
                    {!checkableFlag.comment ? (
                      <TooltipOne title="Dodaj komentarz" placement="top">
                        <IconButton
                          onFocus={() => setIsCommentIconFocused(true)}
                          onBlur={() => setIsCommentIconFocused(false)}
                          disableRipple
                          onClick={() => {
                            handleAddCommentOpen(checkableFlag);
                          }}
                        >
                          <AddCommentIcon fontSize="small" onClick={() => handleAddCommentOpen(checkableFlag)} />
                        </IconButton>
                      </TooltipOne>
                    ) : (
                      <TooltipOne title="Usuń komentarz" placement="top">
                        <IconButton
                          onFocus={() => setIsCommentIconFocused(true)}
                          onBlur={() => setIsCommentIconFocused(false)}
                          disableRipple
                          onClick={() => {
                            handleDeleteComment(checkableFlag);
                          }}
                        >
                          <DeleteCommentIcon fontSize="small" onClick={() => handleDeleteComment(checkableFlag)} />
                        </IconButton>
                      </TooltipOne>
                    )}
                  </Grid>
                )}
              </Grid>
              <Grid display="flex" alignItems="center" padding="10px">
                {checkableFlag.isChecked && checkableFlag.comment && !isDisabled(checkableFlag) && (
                  <TextField
                    value={checkableFlag.comment}
                    onChange={(event) => handleChangeComment(checkableFlag, event?.target.value)}
                    multiline
                    variant="outlined"
                    fullWidth
                    maxRows={2}
                    label="Komentarz"
                    aria-label="komentarz"
                    autoFocus
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      style: {
                        color: isCommentToLong(checkableFlag.comment) ? 'red' : '#2C2D3E',
                      },
                    }}
                    error={isCommentToLong(checkableFlag.comment)}
                    FormHelperTextProps={
                      {
                        component: 'div',
                      } as Partial<FormHelperTextProps<'p'>>
                    }
                    helperText={
                      <ValidationRow
                        helperText={
                          isCommentToLong(checkableFlag.comment) ? `Komentarz jest za długi, max. 256 znaków.` : ''
                        }
                        valueLength={checkableFlag.comment.length}
                        limit={COMMENT_MAX_LENGTH}
                        withError={isCommentToLong(checkableFlag.comment)}
                        limitsVisible={false}
                        helperTextStyle={helperTextStyle}
                      />
                    }
                  />
                )}
              </Grid>
            </Grid>
          );
        })}
        <Grid
          item
          xs={12}
          display="flex"
          flexDirection="row-reverse"
          alignItems="center"
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onClear();
            }
          }}
        >
          <ButtonOne startIcon={<CloseIcon />} onClick={onClear}>
            Wyczyść
          </ButtonOne>
        </Grid>
      </Grid>
      <hr />
    </ModalWithButtons>
  );
};
